import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Layout } from '../components/Layout';
import { FadeInUp, StaggerContainer, StaggerItem } from '../components/AnimatedSection';
import { ExternalLink, Car, Plane, Heart, Shield } from 'lucide-react';

const Industries = () => {
  const { t } = useLanguage();

  const industries = [
    {
      key: 'auto',
      icon: Car,
      link: 'https://www.bmwgroup.com/en/innovation/additive-manufacturing.html',
      image: 'https://images.unsplash.com/photo-1617788138017-80ad40651399?w=600&q=80'
    },
    {
      key: 'aerospace',
      icon: Plane,
      link: 'https://www.airbus.com/en/innovation/digital-design-manufacturing-and-services/additive-manufacturing',
      image: 'https://images.unsplash.com/photo-1767719618628-4a1271ed8ef5?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600'
    },
    {
      key: 'medical',
      icon: Heart,
      link: 'https://www.mayoclinic.org/medical-professionals/orthopedic-surgery/news/3d-printing-in-medicine/mac-20430503',
      image: 'https://images.unsplash.com/photo-1630208566639-fed27bf29fa4?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600'
    },
    {
      key: 'defense',
      icon: Shield,
      link: 'https://www.defense.gov/News/Feature-Stories/Story/Article/3142267/additive-manufacturing-supports-military-readiness/',
      image: 'https://images.unsplash.com/photo-1581093586267-784d0a92223d?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600'
    }
  ];

  return (
    <Layout>
      {/* Hero */}
      <section className="py-24 bg-white" data-testid="industries-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <span className="confidential-badge mb-6 inline-block">
              {t('industries.badge')}
            </span>
          </FadeInUp>
          
          <FadeInUp delay={0.1}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy mb-6" data-testid="industries-title">
              {t('industries.title')}
            </h1>
          </FadeInUp>
          
          <FadeInUp delay={0.2}>
            <p className="text-lg md:text-xl text-[#0066CC] font-medium max-w-3xl">
              {t('industries.subtitle')}
            </p>
          </FadeInUp>
        </div>
      </section>

      {/* Bento Grid */}
      <section className="py-20 bg-slate-50" data-testid="industries-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <StaggerContainer className="grid md:grid-cols-2 gap-6" staggerDelay={0.15}>
            {industries.map((industry) => {
              const IconComponent = industry.icon;
              return (
                <StaggerItem key={industry.key}>
                  <div 
                    className="bg-white border border-slate-200 overflow-hidden hover:border-[#0066CC] transition-colors group"
                    data-testid={`industry-${industry.key}`}
                  >
                    {/* Image Header */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={industry.image}
                        alt={t(`industries.${industry.key}.title`)}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-4 left-4 flex items-center gap-3">
                        <div className="w-10 h-10 bg-white flex items-center justify-center">
                          <IconComponent className="w-5 h-5 text-navy" />
                        </div>
                        <h3 className="text-lg font-bold text-white">
                          {t(`industries.${industry.key}.title`)}
                        </h3>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      <p className="text-xs uppercase tracking-widest text-[#0066CC] font-semibold mb-3">
                        {t(`industries.${industry.key}.company`)}
                      </p>
                      <p className="text-slate-600 text-sm leading-relaxed mb-4">
                        {t(`industries.${industry.key}.desc`)}
                      </p>
                      <a
                        href={industry.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-navy hover:text-[#0066CC] transition-colors"
                      >
                        Learn More
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* Summary Stats */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center p-6 border border-slate-200">
                <p className="text-4xl font-oswald font-bold text-navy">300K+</p>
                <p className="text-xs uppercase tracking-wider text-slate-500 mt-2">BMW Parts/Year</p>
              </div>
              <div className="text-center p-6 border border-slate-200">
                <p className="text-4xl font-oswald font-bold text-navy">1000+</p>
                <p className="text-xs uppercase tracking-wider text-slate-500 mt-2">Airbus Parts Flying</p>
              </div>
              <div className="text-center p-6 border border-slate-200">
                <p className="text-4xl font-oswald font-bold text-navy">55%</p>
                <p className="text-xs uppercase tracking-wider text-slate-500 mt-2">Weight Reduction</p>
              </div>
              <div className="text-center p-6 border border-slate-200">
                <p className="text-4xl font-oswald font-bold text-navy">90%</p>
                <p className="text-xs uppercase tracking-wider text-slate-500 mt-2">Cost Savings</p>
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>
    </Layout>
  );
};

export default Industries;
